import * as React from 'react';
import { Col, Row, Steps, Card } from 'antd';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import * as uuid from 'uuid';
import { observable } from 'mobx';

import { DataLocalService } from 'src/core/services/data-local.service';
import { DateView } from 'src/core/ui/date.view';
import { BooleanView } from 'src/core/ui/boolean.view';
import { DataTableView, Column, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { TextView } from 'src/core/ui/text.view';
import { TimePickerView, DisableKind } from 'src/core/ui/timepicker.view';
import { ProductCategoryEditorController } from 'src/app/products/product-categories/parts/product-category-editor.controller';
import { PlaceEditorController } from 'src/app/general/places/parts/place-editor.controller';
import { AirlineEditorController } from 'src/app/general/airlines/parts/airline-editor.controller';
import { ProductServiceTypeEditorController } from 'src/app/products/product-service-types/parts/product-service-type-editor.controller';
import { SearchTaxonomies } from 'src/app/bookings/search/requests/search-taxonomy';
import { ZuluIcon } from 'src/app/shared/icon';

interface SearchFlightProps {
	currentSearch: SearchFlight;
	fieldsTaxonomy: TaxonomicField[];
	onChangeCategory: (productCategoryId: number) => Promise<void>;
	setBusy(isBusy: boolean);
}

@observer
export class SearchFlightView extends React.Component<SearchFlightProps, {}> {
	@observable
	private dataSet: DataSet<SearchFlightLeg> = new DataLocalService(this.props.currentSearch.flights);

	public componentWillReceiveProps(nextProps: Readonly<SearchFlightProps>) {
		if (this.props.currentSearch.flights.length != nextProps.currentSearch.flights.length) {
			this.dataSet = new DataLocalService(nextProps.currentSearch.flights);
		}
	}

	columns: Column[] = [
		{
			key: 'startDate',
			dataIndex: 'startDate',
			editable: (text: any, record: SearchFlightLeg) => (
				<DateView
					label={null}
					mode="edit"
					value={record.startDate}
					onChanged={(newVal) => {
						const start = moment.utc(record.startDate);
						const isDST = start.isDST() == moment.utc().isDST();
						const oldHours = start.hour();
						const oldMinutes = start.minute();
						record.startDate = newVal;
						record.startDate.setHours(oldHours + (isDST ? 1 : 0))
						record.startDate.setMinutes(oldMinutes);
						let endDate = moment.utc(newVal).endOf('day');
						record.endDate = endDate.toDate();
						let flightReturn = this.props.currentSearch.flights.find(x => x.id == record.idReturn);
						if (flightReturn != null) {
							let startDate = moment.utc(record.startDate).add(record.nights, "day").startOf('day').toDate();
							let endDate = startDate;
							this.updateFlightDefinition(flightReturn, startDate, endDate, flightReturn.fromPlaceId, flightReturn.toPlaceId, flightReturn.nights);
						}
						let flightTo = this.props.currentSearch.flights.find(x => x.idReturn == record.id);
						if (flightTo) {
							let nights = moment.utc(record.startDate).diff(flightTo.startDate, "day");
							this.updateFlightDefinition(flightTo, flightTo.startDate, flightTo.endDate, flightTo.fromPlaceId, flightTo.toPlaceId, nights);
						}
						this.forceUpdate();
					}}
					format="small-date"
					dateUsage="utc"
				/>
			),
			title: 'Departure Date',
			width: 130
		},
		{
			key: 'From',
			dataIndex: 'From',
			editable: (text: any, record: SearchFlightLeg, index: number, isSelected: boolean) => (
				<PlaceEditorController
					label={null}
					mode="edit"
					type={[1]}
					onChanged={(newVal) => {
						if (newVal) {
							record.fromPlaceId = newVal.id;
							record.fromPlace = newVal.displayName;
						} else {
							record.fromPlaceId = null;
							record.fromPlace = null;
						}
						this.forceUpdate();
					}}
					value={record.fromPlaceId}
					setBusy={this.props.setBusy}
				/>
			),
			title: 'From',
		},
		{
			key: 'To',
			dataIndex: 'To',
			editable: (text: any, record: SearchFlightLeg, index: number, isSelected: boolean) => (
				<PlaceEditorController
					label={null}
					mode="edit"
					type={[1]}
					onChanged={(newVal) => {
						if (newVal) {
							record.toPlaceId = newVal.id;
							record.toPlace = newVal.displayName;
						} else {
							record.toPlaceId = null;
							record.toPlace = null;
						}
						this.forceUpdate();
					}}
					value={record.toPlaceId}
					setBusy={this.props.setBusy}
				/>
			),
			title: 'To',
			width: '17%',
		},
		{
			key: 'Nights',
			dataIndex: 'Nights',
			editable: (text: any, record: SearchFlightLeg, index: number, isSelected: boolean) => (
				<TextView
					label={null}
					mode="edit"
					value={record.nights == null ? 0 : record.nights}
					onChanged={(newVal) => {
						record.nights = parseInt(newVal);
						if (record.nights > 0) {
							let startDate = moment.utc(record.startDate).add(record.nights, "day").startOf('day');
							let endDate = startDate.clone().endOf("day");
							let fromId = record.toPlaceId;
							let toId = record.fromPlaceId;
							let flightReturn = this.props.currentSearch.flights.find(x => x.id == record.idReturn);
							if (flightReturn != null) {
								this.updateFlightDefinition(flightReturn, startDate.toDate(), endDate.toDate(), fromId, toId, 0);
							}
							else {
								this.props.setBusy(true);
								this.createFlightDefinition(record, startDate.toDate(), endDate.toDate(), fromId, toId).then(res => {
									this.props.setBusy(false);
								});
							}

						}

					}}
				/>
			),
			title: 'Nights',
			width: 50
		},
		{
			key: 'DepartureTime',
			dataIndex: 'DepartureTime',
			editable: (text: any, record: SearchFlightLeg, index: number, isSelected: boolean) => (
				<TimePickerView
					label={null}
					required
					mode="edit"
					value={record.startDate}
					onChanged={(value) => {
						record.startDate.setHours(value.getHours());
						record.startDate.setMinutes(value.getMinutes());
						this.forceUpdate();
					}}
					disableKind={DisableKind.disableSeconds}
					dateUsage="utc"
				/>
			),
			title: 'Departure Time',
			width: 130
		},
		{
			key: 'Airline',
			dataIndex: 'Airline',
			editable: (text: any, record: SearchFlightLeg, index: number, isSelected: boolean) => (
				<AirlineEditorController
					label={null}
					mode="edit"
					setBusy={this.props.setBusy}
					value={record.airlineId}
					onChanged={newVal => {
						if (newVal) {
							record.airlineId = newVal.id;
							record.airlineName = newVal.title;
						} else {
							record.airlineId = null;
							record.airlineName = null;
						}
						this.forceUpdate();
					}}
				/>
			),
			title: 'Airline',
			width: '15%'
		},
		{
			key: 'Category',
			dataIndex: 'Category',
			editable: (text: any, record: SearchFlightLeg, index: number, isSelected: boolean) => (
				<ProductCategoryEditorController
					label={null}
					mode="edit"
					onChanged={(value) => {
						record.productCategoryId = value != null ? value.id : null;
						record.productServiceTypeId = null;
						this.forceUpdate();
					}}
					value={record.productCategoryId}
					productType={4}
					setBusy={this.props.setBusy}
				/>
			),
			title: 'Category',
			width: '10%'
		},
		{
			key: 'CabinClass',
			dataIndex: 'CabinClass',
			editable: (text: any, record: SearchFlightLeg, index: number, isSelected: boolean) => (
				<ProductServiceTypeEditorController
					label={null}
					categoryId={record.productCategoryId}
					mode={record.productCategoryId ? 'edit' : 'display'}
					onChanged={(value) => {
						record.productServiceTypeId = value != null ? value.id : null;
						record.productServiceTypeDisplayName = value != null ? value.displayName : null;
						this.forceUpdate();
					}}
					value={record.productServiceTypeId}
					setBusy={this.props.setBusy}
				/>
			),
			title: 'Cabin Class',
			width: '10%'
		}
	];

	updateFlightDefinition = (toUpdate: SearchFlightLeg, startDate?: Date, endDate?: Date, fromPlaceId?: number, toPlaceId?: number, nights?: number): void => {
		if (toUpdate) {
			toUpdate.startDate = startDate;
			toUpdate.endDate = endDate;
			toUpdate.fromPlaceId = fromPlaceId;
			toUpdate.toPlaceId = toPlaceId;
			toUpdate.nights = nights;
			if (toUpdate.idReturn == null) {
				this.forceUpdate();
			} else {
				let flightReturn = this.props.currentSearch.flights.find(x => x.id == toUpdate.idReturn);
				let startDate = moment.utc(toUpdate.startDate).add(toUpdate.nights, "day").startOf('day');
				let endDate = startDate.clone().endOf("day");
				this.updateFlightDefinition(flightReturn, startDate.toDate(), endDate.toDate(), flightReturn.fromPlaceId, flightReturn.toPlaceId, flightReturn.nights);
			}
		}
	};

	createFlightDefinition = (
		parent?: SearchFlightLeg,
		startDate?: Date,
		endDate?: Date,
		fromPlaceId?: number,
		toPlaceId?: number
	): Promise<SearchFlightLeg[]> => {
		let endDateMoment = endDate == null ? moment.utc().endOf('day') : moment.utc(endDate).endOf('day');
		let flight: SearchFlightLeg = {
			id: uuid.v4(),
			startDate: startDate == null ? moment.utc().startOf('day').toDate() : startDate,
			endDate: endDateMoment.toDate(),
			fromPlace: null,
			fromPlaceId: fromPlaceId,
			toPlaceId: toPlaceId,
			toPlace: null,
			nights: 0,
			airlineId: null,
			airlineName: null,
		};
		if (parent != null) {
			parent.idReturn = flight.id;
			flight.fromPlace = parent.toPlace;
			flight.toPlace = parent.fromPlace;
		}
		this.props.currentSearch.flights.push(observable(flight));
		return Promise.resolve(this.props.currentSearch.flights);
	};

	deleteFlightDefinition = (item: SearchFlightLeg): void => {
		let index = this.props.currentSearch.flights.findIndex(e => e == item);
		if (index > 0) {
			this.props.currentSearch.flights.splice(index, 1);
		}
	};

	render(): React.ReactNode {
		if (this.props.currentSearch == null) {
			return null;
		}

		const model = this.props.currentSearch;
		const flights = (
			<DataTableView
				rowKey="id"
				mode="edit"
				setBusy={this.props.setBusy}
				columns={this.columns}
				model={this.dataSet}
				createNewItem={this.createFlightDefinition}
				hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination}
				afterDeleteItem={items => {
					for (const item of items) {
						this.deleteFlightDefinition(item);
					}
				}}
			/>
		);

		return (
			<React.Fragment>
				{flights}
				<br />
				<Row gutter={16}>
					<Col xs={24} sm={6}>
						<TextView
							label="Non Stop Flight Count"
							mode="edit"
							value={model.nonStopOverCount}
							onChanged={newVal => {
								model.nonStopOverCount = newVal;
							}}
						/>
					</Col>
					<Col xs={24} sm={6}>
						<BooleanView
							label="Public fares"
							mode="edit"
							value={model.publicFares}
							onChanged={(newVal) => {
								model.publicFares = newVal;
							}}
						/>
					</Col>
					<Col xs={24} sm={6}>
						<BooleanView
							label="Direct Only"
							mode="edit"
							value={model.directOnly}
							onChanged={(newVal) => {
								model.directOnly = newVal;
							}}
						/>
					</Col>
					<Col xs={24} sm={6}>
						<BooleanView
							label="Negociated fares"
							mode="edit"
							value={model.negociatedFares}
							onChanged={newVal => {
								model.negociatedFares = newVal;
							}}
						/>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24} sm={6}>
						<DateView
							label="Last Ticketing Date"
							mode="edit"
							value={model.lastTicketingDate}
							onChanged={newVal => {
								let date = moment.utc(newVal).toDate();
								model.lastTicketingDate = date;
							}}
							format="small-date"
							dateUsage="utc"
						/>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24}>
						<hr />
						<SearchTaxonomies
							model={model}
							fieldsTaxonomy={this.props.fieldsTaxonomy}
							setBusy={this.props.setBusy}
						/>
					</Col>
				</Row>
				<br />
				<Overview flights={this.dataSet.items.slice()} />
			</React.Fragment>
		);
	}
}

@observer
class Overview extends React.Component<{ flights: SearchFlightLeg[] }, {}> {
	public render(): React.ReactNode {
		const flights = this.props.flights;
		const lastFlight = flights[flights.length - 1];

		return (
			<Card title="Overview" size="small">
				<Steps progressDot={true} current={flights.length}>
					{flights.map(this.renderFlight)}
					{this.renderFlight(lastFlight, -1)}
				</Steps>
			</Card>
		);
	}

	private renderFlight(flight: SearchFlightLeg, useDestination: number): React.ReactNode {
		const { airlineName, startDate, endDate, fromPlace, toPlace, id } = flight;

		const description = (
			<Row gutter={16}>
				<Col xs={12}>
					{airlineName || "NOT SET"}
				</Col>
				<Col xs={12}>
					<ZuluIcon type="icon-plane" />
				</Col>
				<Col xs={24}>
					{moment.utc(useDestination === -1 ? endDate : startDate).format("DD-MM-YYYY HH:mm")}
				</Col>
			</Row>
		);
		return (
			<Steps.Step key={id} title={useDestination === -1 ? toPlace : fromPlace || "NOT SET"} description={description} />
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/search/requests/search-flight.view.tsx